import { ref, type Ref } from 'vue'
import { searchFiles } from '@/services/api/file-service'
import type { FileInfo } from '@puppycad/proto'
import {
  DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
  type InsertComponentFixationMode,
} from '@puppycad/cad-core'
import type { InsertCadSourceDescriptor } from '@/services/loaders/insert-cad-loader'
import { logger } from '@/utils'

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_INSERT_KINDS: readonly string[] = ['cad_part', 'cad_assembly']

export interface UseInsertComponentReturn {
  isInsertComponentActive: Ref<boolean>
  insertComponentFiles: Ref<FileInfo[]>
  insertComponentFilterKinds: Ref<string[]>
  insertComponentQuery: Ref<string>
  insertComponentLoading: Ref<boolean>
  insertComponentHasMore: Ref<boolean>
  insertComponentTotal: Ref<number>
  insertComponentPage: Ref<number>
  insertComponentPageSize: Ref<number>
  /** 插入时固定/浮动零部件策略，按 SolidWorks PropertyManager 选项语义传入加载链路。 */
  insertComponentFixationMode: Ref<InsertComponentFixationMode>
  /** PropertyManager 中按用户勾选顺序保存的待插入零部件。 */
  selectedInsertComponentFiles: Ref<FileInfo[]>
  /** 当前正在放置的零部件在队列中的 1-based 序号；无放置流程时为 0。 */
  insertComponentPlacementIndex: Ref<number>
  /** 当前插入队列总数；无放置流程时为 0。 */
  insertComponentPlacementTotal: Ref<number>
  isPlacingComponent: Ref<boolean>
  placingComponentDescriptor: Ref<InsertCadSourceDescriptor | null>
  startInsertComponent: () => void
  loadInsertComponentFiles: () => void
  cancelInsertComponent: () => void
  setInsertComponentFilterKinds: (kinds: string[]) => void
  setInsertComponentQuery: (query: string, kinds?: readonly string[]) => void
  setInsertComponentFixationMode: (mode: InsertComponentFixationMode) => void
  loadInsertComponentPage: (page: number, limit?: number, kinds?: readonly string[]) => void
  loadMoreInsertComponents: () => void
  selectInsertComponent: (file: FileInfo) => void
  toggleInsertComponentSelection: (file: FileInfo) => void
  confirmInsertComponentSelection: () => boolean
  peekNextPlacementDescriptor: () => InsertCadSourceDescriptor | null
  finishCurrentPlacement: () => boolean
  finishPlacement: () => void
}

export function useInsertComponent(): UseInsertComponentReturn {
  const isInsertComponentActive = ref(false)
  const insertComponentFiles = ref<FileInfo[]>([])
  const insertComponentFilterKinds = ref<string[]>([...DEFAULT_INSERT_KINDS])
  const insertComponentQuery = ref('')
  const insertComponentLoading = ref(false)
  const insertComponentHasMore = ref(false)
  const insertComponentTotal = ref(0)
  const insertComponentFixationMode = ref<InsertComponentFixationMode>(
    DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
  )
  const selectedInsertComponentFiles = ref<FileInfo[]>([])
  const insertComponentPlacementQueue = ref<FileInfo[]>([])
  const insertComponentPlacementIndex = ref(0)
  const insertComponentPlacementTotal = ref(0)
  const currentPage = ref(1)
  const insertComponentPageSize = ref(DEFAULT_PAGE_SIZE)
  const isPlacingComponent = ref(false)
  const placingComponentDescriptor = ref<InsertCadSourceDescriptor | null>(null)
  /** 最近一次列表请求编号，用于忽略搜索/分页快速切换产生的过期响应。 */
  let loadRequestId = 0

  /**
   * 归一化后端分页页码。
   *
   * @param page 外部传入页码。
   * @returns 大于 0 的整数页码。
   */
  function normalizePage(page: number): number {
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
  }

  /**
   * 归一化后端分页条数。
   *
   * @param limit 外部传入每页条数。
   * @returns 大于 0 的整数条数。
   */
  function normalizeLimit(limit: number | undefined): number {
    return Number.isFinite(limit) && limit && limit > 0
      ? Math.floor(limit)
      : DEFAULT_PAGE_SIZE
  }

  /**
   * 归一化 CAD 模型搜索类型。
   *
   * 设计说明：插入零部件默认允许零件和装配体；负责分支“基于模型”会按目标节点类型传入单一 kind。
   * 这里统一做空值兜底，保证后续 SearchFilesRequest.kinds 始终来自一个明确入口。
   *
   * @param kinds 外部希望用于 SearchFilesRequest.kinds 的模型类型。
   * @returns 非空的模型类型数组。
   */
  function normalizeFilterKinds(kinds: readonly string[] | undefined): string[] {
    return kinds && kinds.length > 0
      ? [...kinds]
      : [...DEFAULT_INSERT_KINDS]
  }

  /**
   * 判断候选文件是否可作为插入零部件来源。
   *
   * @param file 后端返回的文件项。
   * @returns 文件类型是否属于零件或装配体。
   */
  function isInsertableComponentFile(file: FileInfo): boolean {
    return file.kind === 'cad_part' || file.kind === 'cad_assembly'
  }

  /**
   * 将文件项转换为当前放置流程需要的加载描述。
   *
   * @param file 用户选择的零件或装配体文件。
   * @returns 插入加载描述。
   */
  function createPlacementDescriptor(file: FileInfo): InsertCadSourceDescriptor {
    return {
      projectRootPath: file.fileId,
      selectedFileId: file.fileId,
      cadType: file.kind as 'cad_part' | 'cad_assembly',
      displayName: file.fileId.split('/').pop() ?? file.fileId,
      fixationMode: insertComponentFixationMode.value,
    }
  }

  /**
   * 进入指定文件的放置流程。
   *
   * @param file 当前队列中需要放置的文件。
   * @returns 文件可插入并已写入 descriptor 时返回 true。
   */
  function startPlacementForFile(file: FileInfo): boolean {
    if (!isInsertableComponentFile(file)) {
      return false
    }
    placingComponentDescriptor.value = createPlacementDescriptor(file)
    isPlacingComponent.value = true
    isInsertComponentActive.value = false
    return true
  }

  /**
   * 清理多选队列状态。
   */
  function clearInsertComponentQueue(): void {
    selectedInsertComponentFiles.value = []
    insertComponentPlacementQueue.value = []
    insertComponentPlacementIndex.value = 0
    insertComponentPlacementTotal.value = 0
  }

  /**
   * 加载插入零部件候选页。
   *
   * 设计说明：搜索、分页和“加载更多”都复用同一个 SearchFiles 后端接口。
   * 请求编号用于保证快速切换页码或关键词时，只让最后一次响应落地。
   *
   * @param page 后端页码。
   * @param append 是否追加到已有列表。
   * @param limit 每页条数。
   */
  async function loadPage(page: number, append: boolean, limit = insertComponentPageSize.value): Promise<void> {
    const requestId = loadRequestId + 1
    loadRequestId = requestId
    insertComponentLoading.value = true
    try {
      const nextPage = normalizePage(page)
      const nextLimit = normalizeLimit(limit)
      const response = await searchFiles({
        query: insertComponentQuery.value,
        kinds: insertComponentFilterKinds.value,
        page: nextPage,
        limit: nextLimit,
      })
      if (requestId !== loadRequestId) {
        return
      }
      const files = response.files ?? []
      const total = response.total ?? 0
      if (append) {
        insertComponentFiles.value = [...insertComponentFiles.value, ...files]
      } else {
        insertComponentFiles.value = files
      }
      insertComponentTotal.value = total
      currentPage.value = nextPage
      insertComponentPageSize.value = nextLimit
      insertComponentHasMore.value = insertComponentFiles.value.length < total
    } catch (error) {
      // 列表加载失败时保留已有候选项，避免搜索抖动清空用户上下文；错误仍记录便于定位后端或网络问题。
      logger.error('[useInsertComponent] 加载插入零部件候选失败', {
        page,
        append,
        limit,
        query: insertComponentQuery.value,
        kinds: insertComponentFilterKinds.value,
        error,
      })
    } finally {
      if (requestId === loadRequestId) {
        insertComponentLoading.value = false
      }
    }
  }

  function startInsertComponent(): void {
    isInsertComponentActive.value = true
    insertComponentFiles.value = []
    insertComponentFilterKinds.value = [...DEFAULT_INSERT_KINDS]
    insertComponentQuery.value = ''
    insertComponentTotal.value = 0
    insertComponentHasMore.value = false
    insertComponentFixationMode.value = DEFAULT_INSERT_COMPONENT_FIXATION_MODE
    clearInsertComponentQueue()
    placingComponentDescriptor.value = null
    isPlacingComponent.value = false
    currentPage.value = 1
    insertComponentPageSize.value = DEFAULT_PAGE_SIZE
    void loadPage(1, false)
  }

  /**
   * 仅加载插入零部件候选列表，不进入插入放置流程。
   *
   * 设计说明：负责分支“基于模型”与插入零部件使用同一套 CAD 模型列表，
   * 但打开负责分支弹窗时不能切换 PropertyManager 或激活放置状态，所以这里提供只读候选加载入口。
   */
  function loadInsertComponentFiles(): void {
    if (insertComponentFiles.value.length > 0 || insertComponentLoading.value) {
      return
    }
    insertComponentFilterKinds.value = [...DEFAULT_INSERT_KINDS]
    insertComponentQuery.value = ''
    insertComponentTotal.value = 0
    insertComponentHasMore.value = false
    currentPage.value = 1
    insertComponentPageSize.value = DEFAULT_PAGE_SIZE
    void loadPage(1, false)
  }

  function cancelInsertComponent(): void {
    loadRequestId += 1
    isInsertComponentActive.value = false
    insertComponentFiles.value = []
    insertComponentFilterKinds.value = [...DEFAULT_INSERT_KINDS]
    insertComponentQuery.value = ''
    insertComponentTotal.value = 0
    insertComponentHasMore.value = false
    insertComponentLoading.value = false
    insertComponentFixationMode.value = DEFAULT_INSERT_COMPONENT_FIXATION_MODE
    clearInsertComponentQueue()
    placingComponentDescriptor.value = null
    isPlacingComponent.value = false
    currentPage.value = 1
    insertComponentPageSize.value = DEFAULT_PAGE_SIZE
  }

  function setInsertComponentFilterKinds(kinds: string[]): void {
    insertComponentFilterKinds.value = [...kinds]
    insertComponentFiles.value = []
    // 保留 total，避免后端分页请求过程中分页器临时坍缩为 0 条。
    insertComponentHasMore.value = false
    currentPage.value = 1
    void loadPage(1, false)
  }

  function setInsertComponentQuery(query: string, kinds?: readonly string[]): void {
    insertComponentQuery.value = query
    insertComponentFilterKinds.value = normalizeFilterKinds(kinds ?? insertComponentFilterKinds.value)
    insertComponentFiles.value = []
    // 搜索结果返回前沿用上一轮总数，让分页区域在 loading 期间保持稳定。
    insertComponentHasMore.value = false
    currentPage.value = 1
    void loadPage(1, false)
  }

  function loadInsertComponentPage(
    page: number,
    limit = insertComponentPageSize.value,
    kinds?: readonly string[],
  ): void {
    const nextPage = normalizePage(page)
    const nextLimit = normalizeLimit(limit)
    insertComponentFilterKinds.value = normalizeFilterKinds(kinds)
    insertComponentFiles.value = []
    // 翻页时立即更新目标页，但不清空总数，避免分页按钮在请求期间闪烁。
    insertComponentHasMore.value = false
    currentPage.value = nextPage
    insertComponentPageSize.value = nextLimit
    void loadPage(nextPage, false, nextLimit)
  }

  function loadMoreInsertComponents(): void {
    if (!insertComponentHasMore.value || insertComponentLoading.value) return
    void loadPage(currentPage.value + 1, true)
  }

  function setInsertComponentFixationMode(mode: InsertComponentFixationMode): void {
    insertComponentFixationMode.value = mode
  }

  function selectInsertComponent(file: FileInfo): void {
    clearInsertComponentQueue()
    if (!isInsertableComponentFile(file)) {
      return
    }
    insertComponentPlacementQueue.value = [file]
    insertComponentPlacementIndex.value = 1
    insertComponentPlacementTotal.value = 1
    void startPlacementForFile(file)
  }

  /**
   * 切换 PropertyManager 列表中的待插入勾选状态。
   *
   * 设计说明：顺序插入必须保留用户勾选顺序，因此这里使用数组追加/移除，而不是每次按列表可见顺序重算。
   *
   * @param file 用户点击的候选文件。
   */
  function toggleInsertComponentSelection(file: FileInfo): void {
    if (!isInsertableComponentFile(file)) {
      return
    }
    const existingIndex = selectedInsertComponentFiles.value
      .findIndex((selectedFile) => selectedFile.fileId === file.fileId)
    if (existingIndex >= 0) {
      selectedInsertComponentFiles.value = selectedInsertComponentFiles.value
        .filter((_, index) => index !== existingIndex)
      return
    }
    selectedInsertComponentFiles.value = [...selectedInsertComponentFiles.value, file]
  }

  /**
   * 确认当前勾选列表并启动顺序放置队列。
   *
   * @returns 成功进入第一个零部件放置流程时返回 true。
   */
  function confirmInsertComponentSelection(): boolean {
    if (selectedInsertComponentFiles.value.length === 0) {
      return false
    }
    insertComponentPlacementQueue.value = [...selectedInsertComponentFiles.value]
    insertComponentPlacementIndex.value = 1
    insertComponentPlacementTotal.value = insertComponentPlacementQueue.value.length
    const started = startPlacementForFile(insertComponentPlacementQueue.value[0])
    if (!started) {
      clearInsertComponentQueue()
    }
    return started
  }

  /**
   * 读取当前队列下一项的放置描述，用于在用户放置当前项时预加载下一项。
   *
   * @returns 队列下一项 descriptor；不存在下一项时返回 null。
   */
  function peekNextPlacementDescriptor(): InsertCadSourceDescriptor | null {
    const nextFile = insertComponentPlacementQueue.value[1]
    if (!nextFile || !isInsertableComponentFile(nextFile)) {
      return null
    }
    return createPlacementDescriptor(nextFile)
  }

  /**
   * 当前零部件提交完成后推进到队列中的下一个零部件。
   *
   * @returns 存在下一项并已进入放置流程时返回 true；队列结束时返回 false。
   */
  function finishCurrentPlacement(): boolean {
    if (insertComponentPlacementQueue.value.length === 0) {
      finishPlacement()
      return false
    }
    insertComponentPlacementQueue.value = insertComponentPlacementQueue.value.slice(1)
    if (insertComponentPlacementQueue.value.length === 0) {
      finishPlacement()
      return false
    }
    insertComponentPlacementIndex.value = insertComponentPlacementTotal.value
      - insertComponentPlacementQueue.value.length
      + 1
    return startPlacementForFile(insertComponentPlacementQueue.value[0])
  }

  function finishPlacement(): void {
    isPlacingComponent.value = false
    placingComponentDescriptor.value = null
    clearInsertComponentQueue()
    cancelInsertComponent()
  }

  return {
    isInsertComponentActive,
    insertComponentFiles,
    insertComponentFilterKinds,
    insertComponentQuery,
    insertComponentLoading,
    insertComponentHasMore,
    insertComponentTotal,
    insertComponentPage: currentPage,
    insertComponentPageSize,
    insertComponentFixationMode,
    selectedInsertComponentFiles,
    insertComponentPlacementIndex,
    insertComponentPlacementTotal,
    isPlacingComponent,
    placingComponentDescriptor,
    startInsertComponent,
    loadInsertComponentFiles,
    cancelInsertComponent,
    setInsertComponentFilterKinds,
    setInsertComponentQuery,
    setInsertComponentFixationMode,
    loadInsertComponentPage,
    loadMoreInsertComponents,
    selectInsertComponent,
    toggleInsertComponentSelection,
    confirmInsertComponentSelection,
    peekNextPlacementDescriptor,
    finishCurrentPlacement,
    finishPlacement,
  }
}
